import { useEffect, useState } from 'react';
import type {
  CreateReleaseRequest,
  CreateCampaignRequest,
  Release,
  RolloutRing,
} from '@hospasense/contracts';
import { Button, Modal, Input, Select, Textarea } from '../components/ui/index.js';

export function CreateReleaseModal({
  isOpen,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateReleaseRequest) => Promise<void>;
}) {
  const [version, setVersion] = useState('');
  const [title, setTitle] = useState('');
  const [channel, setChannel] = useState<'stable' | 'rc' | 'hotfix'>('stable');
  const [releaseNotes, setReleaseNotes] = useState('');
  const [minCompatibleVersion, setMinCompatibleVersion] = useState('v4.0.0');
  const [migrationRequired, setMigrationRequired] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        version,
        title,
        channel,
        releaseNotes,
        breakingChanges: [],
        minCompatibleVersion,
        migrationRequired,
        artifacts: [
          {
            component: 'api',
            artifactName: `hospasense-api-${version}.tar.gz`,
            imageDigest: 'sha256:e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
            sha256Checksum: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
          },
        ],
      });
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={isOpen} title="Create Release Draft" onClose={onClose} wide>
      {error && <div className="mb-2 rounded-lg bg-error-bg p-2 text-xs text-error">{error}</div>}

      <form onSubmit={handleSubmit} className="space-y-3" data-testid="create-release-form">
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Version"
            placeholder="2.6.0"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            required
          />
          <Select
            label="Channel"
            value={channel}
            onChange={(e) => setChannel(e.target.value as 'stable' | 'rc' | 'hotfix')}
          >
            <option value="stable">Stable</option>
            <option value="rc">Release Candidate (RC)</option>
            <option value="hotfix">Hotfix</option>
          </Select>
        </div>

        <Input
          label="Title"
          placeholder="Quarterly Clinical Gateway Update"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />

        <Textarea
          label="Release Notes"
          rows={3}
          value={releaseNotes}
          placeholder="e.g. Added DICOM compression"
          onChange={(e) => setReleaseNotes(e.target.value)}
          required
        />

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Min Compatible Version"
            value={minCompatibleVersion}
            onChange={(e) => setMinCompatibleVersion(e.target.value)}
            required
          />
          <div className="flex items-center pt-5">
            <label className="flex items-center gap-2 text-xs font-semibold text-text-secondary cursor-pointer">
              <input
                type="checkbox"
                checked={migrationRequired}
                onChange={(e) => setMigrationRequired(e.target.checked)}
                className="rounded border-border bg-bg-s1 text-primary focus:ring-accent"
              />
              Migration Required
            </label>
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            loading={submitting}
            data-testid="submit-release-btn"
          >
            Create Draft
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function CreateCampaignModal({
  isOpen,
  releases,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  releases: Release[];
  onClose: () => void;
  onSubmit: (data: CreateCampaignRequest) => Promise<void>;
}) {
  const publishedReleases = releases.filter((r) => r.status === 'published');
  const [name, setName] = useState('');
  const [releaseId, setReleaseId] = useState('');
  const [targetRing, setTargetRing] = useState<RolloutRing>('pilot');
  const [maxErrorRatePercent, setMaxErrorRatePercent] = useState(1.0);
  const [autoRollbackOnFailure, setAutoRollbackOnFailure] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (publishedReleases.length > 0 && !releaseId) {
      setReleaseId(publishedReleases[0]!.id);
    }
  }, [publishedReleases, releaseId]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const selectedReleaseId = releaseId || publishedReleases[0]?.id;
      if (!selectedReleaseId) {
        throw new Error('Please select a published release.');
      }

      await onSubmit({
        name,
        releaseId: selectedReleaseId,
        targetRing,
        healthGate: {
          maxErrorRatePercent,
          minHealthyHeartbeatsPercent: 95.0,
          maxDegradedComponents: 0,
          autoRollbackOnFailure,
        },
      });
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={isOpen} title="Launch Rollout Campaign" onClose={onClose} wide>
      {error && <div className="mb-2 rounded-lg bg-error-bg p-2 text-xs text-error">{error}</div>}

      <form onSubmit={handleSubmit} className="space-y-3" data-testid="create-campaign-form">
        <Input
          label="Campaign Name"
          placeholder="e.g. Q3 Pilot Rollout"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />

        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Published Release"
            value={releaseId}
            onChange={(e) => setReleaseId(e.target.value)}
            required
          >
            {publishedReleases.map((r) => (
              <option key={r.id} value={r.id}>
                {r.version} - {r.title}
              </option>
            ))}
          </Select>
          <Select
            label="Target Ring"
            value={targetRing}
            onChange={(e) => setTargetRing(e.target.value as RolloutRing)}
          >
            <option value="internal">Internal (Canary)</option>
            <option value="pilot">Pilot Ring</option>
            <option value="general">General Fleet</option>
          </Select>
        </div>

        <div className="rounded-lg border border-border bg-bg-s1 p-3">
          <h4 className="text-xs font-bold uppercase tracking-wider text-text-muted">
            Health Gate Criteria
          </h4>
          <div className="mt-2 grid grid-cols-2 gap-3">
            <Input
              label="Max Error Rate (%)"
              type="number"
              value={String(maxErrorRatePercent)}
              onChange={(e) => setMaxErrorRatePercent(parseFloat(e.target.value))}
              required
            />
            <div className="flex items-center pt-4">
              <label className="flex items-center gap-2 text-xs font-semibold text-text-secondary cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoRollbackOnFailure}
                  onChange={(e) => setAutoRollbackOnFailure(e.target.checked)}
                  className="rounded border-border bg-bg-s1 text-primary focus:ring-accent"
                />
                Auto-Rollback on Breach
              </label>
            </div>
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            loading={submitting}
            data-testid="submit-campaign-btn"
          >
            Launch Campaign
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function ActionReasonModal({
  isOpen,
  title,
  actionLabel,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  title: string;
  actionLabel: string;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit(reason);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={isOpen} title={title} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-3">
        <Input
          label="Reason"
          placeholder="Explain why this action is being taken..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" loading={submitting}>
            {actionLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
